/**
 * MathHtmlRenderer — rendu HTML des énoncés/corrigés d'exercices.
 *
 * Extrait tel quel d'ExerciseGeneratorPage pour être partagé avec l'atelier :
 * la parité pédagogique (fractions empilées, LaTeX, styles premium inline,
 * SVG) repose sur CE code — ne pas « simplifier » sans re-tester les
 * générateurs premium (.prem-root) et les figures.
 */
import React from "react";
import MathRenderer from "../components/MathRenderer";
import { formatFractionsHtml } from "./formatFractionsHtml";

const MathHtmlRenderer = ({ html, className = "" }) => {
  if (!html) {
    return null;
  }

  // Pour les générateurs premium avec styles inline, utiliser directement
  // dangerouslySetInnerHTML pour préserver les balises <style> et les classes CSS
  const hasPremiumStyles = html.includes('<style>') || html.includes('prem-root') || html.includes('data-family');

  if (hasPremiumStyles) {
    return (
      <div
        className={`math-html-renderer ${className}`}
        dangerouslySetInnerHTML={{ __html: formatFractionsHtml(html) }}
      />
    );
  }

  const renderMixedContent = (htmlContent) => {
    const preparedHtml = formatFractionsHtml(htmlContent);
    const parser = new DOMParser();
    const doc = parser.parseFromString(preparedHtml, 'text/html');

    const processNode = (node, index = 0) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text && text.trim()) {
          const hasLatex = /\\(?:frac|sqrt|times|div|pm|cdot|leq|geq|neq|approx|[a-zA-Z]+)\{/.test(text) ||
                          /\^{[^}]+}/.test(text) ||
                          /_\{[^}]+\}/.test(text);

          if (hasLatex) {
            return <MathRenderer key={`math-${index}`} content={text} className="inline" />;
          }
          return <span key={`text-${index}`}>{text}</span>;
        }
        return null;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        // Préserver les balises style et script pour les générateurs premium
        if (tagName === 'style' || tagName === 'script') {
          return (
            <span
              key={`html-${index}`}
              dangerouslySetInnerHTML={{ __html: node.outerHTML }}
            />
          );
        }

        const rawHtmlElements = ['svg', 'table', 'img', 'br', 'hr'];
        if (rawHtmlElements.includes(tagName)) {
          // SVGs need a block-level wrapper (div) for proper max-width/max-height containment
          const WrapperTag = tagName === 'svg' ? 'div' : 'span';
          return (
            <WrapperTag
              key={`html-${index}`}
              className={tagName === 'svg' ? 'exercise-figure-svg' : undefined}
              dangerouslySetInnerHTML={{ __html: node.outerHTML }}
            />
          );
        }

        const children = Array.from(node.childNodes).map((child, i) =>
          processNode(child, index * 100 + i)
        ).filter(Boolean);

        const props = { key: `elem-${index}` };
        if (node.className) props.className = node.className;
        if (node.id) props.id = node.id;

        // Préserver les attributs style pour les styles inline
        if (node.hasAttribute('style')) {
          props.style = {};
          const styleAttr = node.getAttribute('style');
          if (styleAttr) {
            styleAttr.split(';').forEach(rule => {
              const [key, value] = rule.split(':').map(s => s.trim());
              if (key && value) {
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                props.style[camelKey] = value;
              }
            });
          }
        }

        // Préserver data-* attributes
        Array.from(node.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            props[attr.name] = attr.value;
          }
        });

        const reactTagMap = {
          'div': 'div', 'p': 'p', 'span': 'span', 'strong': 'strong', 'b': 'b',
          'em': 'em', 'i': 'i', 'ol': 'ol', 'ul': 'ul', 'li': 'li',
          'h1': 'h1', 'h2': 'h2', 'h3': 'h3', 'h4': 'h4', 'h5': 'h5', 'h6': 'h6',
          'a': 'a', 'sup': 'sup', 'sub': 'sub'
        };

        const ReactTag = reactTagMap[tagName] || 'span';
        return React.createElement(ReactTag, props, children.length > 0 ? children : null);
      }

      return null;
    };

    const bodyChildren = Array.from(doc.body.childNodes).map((node, i) =>
      processNode(node, i)
    ).filter(Boolean);

    return bodyChildren;
  };

  return (
    <div className={`math-html-renderer ${className}`}>
      {renderMixedContent(html)}
    </div>
  );
};

export default MathHtmlRenderer;
